import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireVerified } from '@/components/auth/RequireVerified'
import { useAuth } from '@/hooks/use-auth'
import { useLetterConfig } from '@/hooks/use-letter-config'
import { callableErrorMessage } from '@/lib/callable-errors'
import { createLetterPackPaymentIntent } from '@/lib/callables'
import { cn } from '@/lib/utils'
import type { LetterPack } from '@/types/models'

const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? ''
const stripePromise: Promise<Stripe | null> | null = publishableKey
  ? loadStripe(publishableKey)
  : null

function formatPackPrice(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100)
}

function bestValueSku(packs: LetterPack[]): string | null {
  const multipacks = packs.filter((p) => p.letterCount > 1)
  if (multipacks.length === 0) return null
  let best = multipacks[0]
  let bestPer = best.priceCents / best.letterCount
  for (const pack of multipacks.slice(1)) {
    const per = pack.priceCents / pack.letterCount
    if (per < bestPer) {
      best = pack
      bestPer = per
    }
  }
  return best.sku
}

export function LettersPage() {
  return (
    <RequireVerified>
      <LettersContent />
    </RequireVerified>
  )
}

function LettersContent() {
  const { profile } = useAuth()
  const { config, loading, error } = useLetterConfig()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedSku, setSelectedSku] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [purchaseNotice, setPurchaseNotice] = useState<string | null>(null)
  const [balancePulse, setBalancePulse] = useState(false)
  const [paymentExiting, setPaymentExiting] = useState(false)

  const credits = profile?.letterCredits ?? 0
  const creditsBeforePayRef = useRef<number | null>(null)
  const awaitingCreditRef = useRef(false)

  const packs = config.packs
  const selectedPack = useMemo(
    () => packs.find((p) => p.sku === selectedSku) ?? null,
    [packs, selectedSku],
  )
  const bestSku = useMemo(() => bestValueSku(packs), [packs])

  const whitelisted =
    config.whitelistUids.length === 0 ||
    (profile?.id != null && config.whitelistUids.includes(profile.id))

  // Return from Stripe redirect (`?paid=1`): watch balance bump.
  useEffect(() => {
    if (searchParams.get('paid') !== '1') return
    awaitingCreditRef.current = true
    creditsBeforePayRef.current = credits
    setPurchaseNotice('Payment received — updating your balance…')
    setClientSecret(null)
    setSelectedSku(null)
    const next = new URLSearchParams(searchParams)
    next.delete('paid')
    setSearchParams(next, { replace: true })
    // Intentionally capture credits once when `paid=1` appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- credits snapshot at redirect return
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!awaitingCreditRef.current) return
    const before = creditsBeforePayRef.current
    if (before == null) return
    if (credits > before) {
      const added = credits - before
      setPurchaseNotice(
        `Added ${added} letter credit${added === 1 ? '' : 's'}. Balance: ${credits}.`,
      )
      setBalancePulse(true)
      awaitingCreditRef.current = false
      creditsBeforePayRef.current = null
      const t = window.setTimeout(() => setBalancePulse(false), 1600)
      return () => window.clearTimeout(t)
    }
  }, [credits])

  async function startCheckout(pack: LetterPack) {
    if (!stripePromise) {
      setActionError('Stripe is not configured (missing VITE_STRIPE_PUBLISHABLE_KEY).')
      return
    }
    setBusy(true)
    setActionError(null)
    setPurchaseNotice(null)
    setSelectedSku(pack.sku)
    setClientSecret(null)
    try {
      const result = await createLetterPackPaymentIntent(pack.sku)
      setClientSecret(result.clientSecret)
    } catch (e) {
      setActionError(callableErrorMessage(e))
      setSelectedSku(null)
    } finally {
      setBusy(false)
    }
  }

  const closePaymentModal = useCallback(() => {
    if (paymentExiting) return
    setPaymentExiting(true)
    window.setTimeout(() => {
      setClientSecret(null)
      setSelectedSku(null)
      setPaymentExiting(false)
    }, 260)
  }, [paymentExiting])

  function onPaymentSucceeded() {
    creditsBeforePayRef.current = credits
    awaitingCreditRef.current = true
    setPurchaseNotice('Payment successful — updating your balance…')
    closePaymentModal()
  }

  useEffect(() => {
    if (!clientSecret) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePaymentModal()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [clientSecret, closePaymentModal])

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!config.enabled) {
    return (
      <EmptyState
        title="Letter packs unavailable"
        description={
          error ||
          'Physical letters aren’t available for purchase right now. Check back soon.'
        }
      />
    )
  }

  if (!whitelisted) {
    return (
      <EmptyState
        title="Not available for your account yet"
        description="Letter purchases are rolling out gradually. You’re not on the early-access list yet."
      />
    )
  }

  if (packs.length === 0) {
    return (
      <EmptyState
        title="Letter packs unavailable"
        description="No letter packs are configured at the moment. Check back soon."
      />
    )
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold text-navy">Letter credits</h1>
        <p className="mt-1 text-sm text-muted">{config.upsell.body}</p>
        <p
          className={cn(
            'mt-3 inline-flex rounded-full px-3 py-1 text-sm font-semibold transition',
            balancePulse ? 'bg-biddle-blue text-white' : 'bg-surface text-navy',
          )}
        >
          Balance: {credits} credit{credits === 1 ? '' : 's'}
        </p>
      </div>

      {purchaseNotice ? <p className="text-sm font-medium text-success">{purchaseNotice}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle>{config.upsell.headline}</CardTitle>
          <CardDescription>Choose a pack, then pay securely with Stripe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {packs.map((pack) => {
            const isBest = pack.sku === bestSku
            const selected = selectedSku === pack.sku
            return (
              <button
                key={pack.sku}
                type="button"
                disabled={busy || Boolean(clientSecret)}
                onClick={() => void startCheckout(pack)}
                className={cn(
                  'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition',
                  selected
                    ? 'border-biddle-blue bg-blue-50'
                    : 'border-border hover:border-biddle-blue',
                  (busy || clientSecret) && 'opacity-60',
                )}
              >
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-navy">
                      {pack.letterCount} letter{pack.letterCount === 1 ? '' : 's'}
                    </p>
                    {isBest ? (
                      <span className="rounded-full bg-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        Best value
                      </span>
                    ) : null}
                  </div>
                  <p className="text-xs text-muted">
                    {formatPackPrice(Math.round(pack.priceCents / pack.letterCount))} each
                  </p>
                </div>
                <p className="font-semibold text-navy">{formatPackPrice(pack.priceCents)}</p>
              </button>
            )
          })}
        </CardContent>
      </Card>

      {actionError ? <p className="text-sm text-danger">{actionError}</p> : null}

      {clientSecret && stripePromise && selectedPack
        ? createPortal(
            <div
              className={cn(
                'fixed inset-0 z-50 flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-4',
                paymentExiting ? 'map-sheet-backdrop-exit' : 'map-sheet-backdrop-enter',
              )}
              role="dialog"
              aria-modal="true"
              aria-labelledby="letter-payment-title"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) closePaymentModal()
              }}
            >
              <div
                className={cn(
                  'relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl',
                  paymentExiting
                    ? 'property-profile-panel-exit'
                    : 'property-profile-panel-enter',
                )}
              >
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="absolute right-3 top-3 z-10 rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
                  aria-label="Close payment"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-5">
                  <div className="pr-8">
                    <h2
                      id="letter-payment-title"
                      className="text-xl font-bold text-navy"
                    >
                      Payment
                    </h2>
                    <p className="mt-1 text-sm text-muted">
                      Paying {formatPackPrice(selectedPack.priceCents)} for{' '}
                      {selectedPack.letterCount} letter
                      {selectedPack.letterCount === 1 ? '' : 's'}.
                    </p>
                  </div>
                  <div className="mt-5">
                    <Elements stripe={stripePromise} options={{ clientSecret }}>
                      <CheckoutForm
                        onCancel={closePaymentModal}
                        onSucceeded={onPaymentSucceeded}
                      />
                    </Elements>
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

function CheckoutForm({
  onCancel,
  onSucceeded,
}: {
  onCancel: () => void
  onSucceeded: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onPay(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true)
    setError(null)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/letters?paid=1`,
        },
        redirect: 'if_required',
      })
      if (result.error) {
        setError(result.error.message ?? 'Payment failed')
      } else {
        onSucceeded()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onPay} className="space-y-4">
      <PaymentElement />
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={!stripe || busy}>
          {busy ? <Spinner className="border-white border-t-transparent" /> : null}
          Pay now
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
